'use client'

import React, { useState, useEffect, useRef, useMemo } from 'react';
import styles from "./HeroSection.module.scss";
import Image from 'next/image';
import { Modal } from '@components/atoms/Modal';
import Button from '@components/atoms/Button';
import Text from '@components/atoms/Text';
import { useRouter } from 'next/navigation';
import { getLoginUrl } from '@utils/getLoginUrl';
import { useGlobalLoader } from '@hooks/useGlobalLoader';
import { isAfterEndDate, getContestUI, isBeforeRevealDate } from '@helpers/contestLogic';
import { trackEventWithCommonProps, EventType } from '@utils/eventTracker';
import { PAGE_TYPES } from '@constants/pageTypes';
import assetLoader from '@utils/internalImageLoder';
import { closeWebView, updateAppBar } from "@hooks/useWebviewCallback";
import { useWishlist } from '@context/WishlistContext';
import { useIsMobile } from '@hooks/use-mobile';
import { getMonthFromDate } from '@helpers/dateFormatter';
import { inApp } from '@utils/platformDetection.client';
import { CARDROP_HEADER_APPBAR_CONFIG } from '@utils/appbarConfig';

const HeroSection = ({ 
  contestState, 
  isLoggedIn, 
  campaignData, 
  upcomingCampaignData, 
  cityName, 
  campaign_stage, 
  car_policyholder, 
  campaignOptInData, 
  isUserParticipated ,
}: any) => {

  const { wishlistItems } = useWishlist();
  const campaignWishlistCount = wishlistItems.length;

  const isInApp = inApp();

  const router = useRouter();
  const [scrollProgress, setScrollProgress] = useState(0);
  const ticking = useRef(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const [showLeavingModal, setShowLeavingModal] = useState<boolean>(false);
  const { showLoader } = useGlobalLoader();
  const isMobile = useIsMobile();

  updateAppBar(CARDROP_HEADER_APPBAR_CONFIG);
  
  const uiConfig = useMemo(() => {
    return getContestUI(campaignData, upcomingCampaignData, contestState, campaignWishlistCount, isLoggedIn, campaignOptInData, isUserParticipated);
  }, [campaignData, upcomingCampaignData, contestState, campaignWishlistCount, isLoggedIn, campaignOptInData, isUserParticipated]);

  const isContestClosedWithNoUpcoming = useMemo(() => {
    return !upcomingCampaignData && (contestState === "NO_ACTIVE_CAMPAIGN" || contestState === "COMPLETED");
  }, [upcomingCampaignData, contestState]);

  const beforeReveal = isBeforeRevealDate(campaignData?.metadata?.productRevealDate);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const maxScroll = 150;
          const progress = Math.min(scrollY / maxScroll, 1);
          
          setScrollProgress(progress);
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const logoWidth = 63 - (scrollProgress * 24);
  const logoMarginTop = 42 - (scrollProgress * 36);

  const handleCloseContest = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.CAR_DROP_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "back_car_drop",
        city_name: cityName,
        campaign_stage: campaign_stage,
        car_policyholder: car_policyholder,
        campaign_name: "car_drop"
      },
    });
    if (!isLoggedIn || isUserParticipated || contestState === "ALREADY_PARTICIPATED" || contestState === "NEW_PARTICIPANT" || contestState === "NO_ACTIVE_POLICY" || isAfterEndDate(campaignData.endDate)) {
      closeWebView(() => router.back());
    } else {
      setShowLeavingModal(true);
    }
  }

  const handleWishlistingCars = async () => {
    if (isLoggedIn) {
      showLoader();
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_wishlist_modal",
          city_name: cityName,
          campaign_stage: campaign_stage,
          car_policyholder: car_policyholder,
          campaign_name: "car_drop"
        },
      });
      router.push("/campaign-cars/");
    } else {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: PAGE_TYPES.CAR_DROP_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_wishlist_modal",
          city_name: cityName,
          campaign_name: "car_drop"
        },
      });
      window.location.href = getLoginUrl(window.location.href);
    }
  }

  const handleRouteBack = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.CAR_DROP_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_close_campaign",
        city_name: cityName,
        campaign_stage: campaign_stage,
        car_policyholder: car_policyholder,
        campaign_name: "car_drop"
      },
    });
    closeWebView(() => router.back());
  }

  return (
    <>
      <div className={beforeReveal ? `${styles.carDropContainerBeforeReveal}` : `${styles.carDropContainerAfterReveal}`}>
        <div
          ref={headerRef}
          className={styles.carDropContainerHeader}
          style={{
            backgroundColor: `rgba(7, 8, 13, ${scrollProgress})`
          }}
        >
          {
            !isInApp &&
              <Image
                src={`${assetLoader()}/icons/Chevron_left_light.svg`}
                alt='arrow icon'
                width={24}
                height={24}
                loading='eager'
                className={styles.carDropBackIcon}
                onClick={handleCloseContest}
              />
          }
          <div
            className={styles.carDropLogoWrapper}
            style={{
              width: `${logoWidth}%`,
              marginTop: `${isMobile ? `${logoMarginTop}px` : '0px'}`
            }}
          >
            <Image
              src={`${assetLoader()}/images/CarDrop/CarDropLogo_${getMonthFromDate(campaignData?.startDate)}.webp`}
              alt='arrow icon'
              fill
              loading='eager'
              className={styles.carDropLogo}
            />
          </div>
        </div>
        {
          !beforeReveal ?
            // isMobile ?
              <div className={styles.videoWrapper}>
                <video
                  src={`${assetLoader()}/images/CarDrop/CarDropACR_Jan.webm`}
                  className={styles.carDropHeroMobileVideo}
                  autoPlay
                  muted
                  playsInline
                  preload="auto"
                />
              </div>
              // :
              // <video
              //   // src={`${assetLoader()}/CarDropACR_Dec.webm`}
              //   src={`/images/CarDrop/CarDropACR_Jan.webm`}
              //   className={styles.carDropHeroVideo}
              //   autoPlay
              //   muted
              //   playsInline
              //   preload="auto"
              // />
            :
              <div className={styles.videoWrapperBeforeReveal}>
                <video
                  src={`${assetLoader()}/images/CarDrop/CarDropACR_Jan.webm`}
                  className={styles.carDropHeroVideoBeforeReveal}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                />
              </div>
        }
        {
          !beforeReveal &&
            <Image
              src={`${assetLoader()}/images/CarDrop/ACR_CarName_Jan.webp`}
              alt='arrow icon'
              fill
              loading='eager'
              className={styles.revealLogoDesktop}
            />
        }
        <div className={styles.HeadingContentMobile}>
          {!beforeReveal &&
            <Image
              src={`${assetLoader()}/images/CarDrop/ACR_CarName_Jan.webp`}
              alt='arrow icon'
              fill
              loading='eager'
              className={styles.revealLogo}
            />
          }
          {
            uiConfig.title &&
            <Text className={uiConfig.title.includes("Closed") ? styles.RedTitle : styles.Title}>{uiConfig.title}</Text>
          }
          <Text className={styles.SubTitle}>
            {isContestClosedWithNoUpcoming
              ? "You just missed this one"
              : uiConfig.useUpcomingDates && upcomingCampaignData?.startDate
                ? `${uiConfig.subtitle}`
                : uiConfig.subtitle
            }
          </Text>
          {uiConfig.additionalSubTitle && (
            <Text className={styles.additionalSubTitle}>
              {uiConfig.additionalSubTitle}
            </Text>
          )}
        </div>
      </div>


      <Modal
        isOpen={showLeavingModal}
        onClose={() => setShowLeavingModal(false)}
        overlayClassName={styles.modalOverlay}
        contentClassName={styles.modalContent}
        headerClassName={styles.modalHeader}
      >
        <div className={styles.leavingModalContainer}>
          <Image
            src={`${assetLoader()}/icons/LeavingCaution.svg`}
            alt="howWishlistDo"
            width={56}
            height={56}
          />
          <div className={styles.leavingModalContent}>
            <Text className={styles.leavingModalHead}>Leaving already? You’re almost in.</Text>
            <Text className={styles.leavingModalSubHead}>Add cars to your wishlist and you could win one.</Text>
          </div>
          <Button
            className={styles.modalButton}
            onClick={handleWishlistingCars}
            aria-label="Close savings modal"
          >
            {campaignWishlistCount < 5 && campaignOptInData ? "Finish wishlisting cars" : "Start wishlisting cars"}
          </Button>
          <p className={styles.leavingModalLater} onClick={handleRouteBack}>I&apos;ll wishlist later</p>
        </div>
      </Modal>
    </>
  )
}

export default HeroSection;